import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge } from '../components/Common';

const AGENT_TYPES = [
  { id: 'planner', label: 'Planner', description: 'Decomposes objectives into phases and assigns specialist agents' },
  { id: 'recon', label: 'Recon', description: 'Discovers endpoints, crawls pages, fingerprints technology' },
  { id: 'exploit', label: 'Exploit', description: 'Probes for vulnerabilities with targeted payloads' },
  { id: 'code_review', label: 'Code Review', description: 'Analyzes source code for security issues' },
  { id: 'report', label: 'Report Writer', description: 'Writes finding descriptions and remediation guidance' },
  { id: 'mentor', label: 'Mentor', description: 'Monitors for stuck loops and suggests alternatives' },
];

const DEFAULT_PROMPTS = {
  planner: `You are a senior penetration test lead creating a test plan.
Break the engagement into 3-7 ordered phases. Each phase specifies the specialist agent, objective, focus areas, and budget.
Standard phases: recon → auth testing → input validation → authorization → business logic → report.`,

  recon: `You are a reconnaissance specialist. Map the full attack surface.
Focus on: endpoint discovery, technology fingerprinting, authentication detection, WAF identification.
Use common path enumeration, link extraction, and SPA route discovery.`,

  exploit: `You are an autonomous penetration tester. Based on the observation, decide the SINGLE most valuable next action.
Return JSON: {"action_type": "probe|exploit|validate|recon", "target": "URL", "test_type": "sqli|xss|idor|auth_bypass|ssrf|lfi|rce|info_disclosure|csrf|header_check", "reasoning": "why", "method": "GET|POST", "payloads": ["..."], "parameter": "param_name"}
IMPORTANT: Do NOT repeat the same action. Move through test types systematically. If all tested, return {"action_type": "done"}.`,

  code_review: `You are a security code reviewer. Analyze code for vulnerabilities.
Look for: hardcoded secrets, SQL injection, XSS, insecure deserialization, command injection, path traversal, SSRF, insecure crypto, debug endpoints.
Report only confirmed issues with severity and remediation.`,

  report: `You are a senior penetration tester writing findings for an executive audience.
For each finding, provide: clear description, business impact, step-by-step remediation, CVSS 3.1 score with vector, and OWASP category.`,

  mentor: `You are a senior pentester mentoring a junior. When the agent is stuck or inefficient, provide specific, actionable alternative strategies.
Detect: repeated identical actions, stale recon, no findings after many steps, budget waste.`,
};

const DEFAULT_PAYLOADS = {
  sqli: ["' OR '1'='1", "1; DROP TABLE test--", "' UNION SELECT NULL--", "1' AND '1'='1"],
  xss: ["<script>alert(1)</script>", "<img src=x onerror=alert(1)>", "'\"><svg/onload=alert(1)>"],
  lfi: ["../../../../etc/passwd", "....//....//etc/passwd", "/etc/passwd%00"],
  ssrf: ["http://169.254.169.254/latest/meta-data/", "http://127.0.0.1:80"],
  rce: ["; id", "| id", "`id`", "$(id)"],
  info_disclosure: ["../.env", "../.git/config"],
  auth_bypass: ["admin' --", "admin'/*"],
  idor: ["1", "0", "999999", "../1"],
};

const DEFAULT_COMMON_PATHS = [
  "/robots.txt", "/sitemap.xml", "/.well-known/security.txt",
  "/api", "/api/v1", "/admin", "/login", "/wp-login.php",
  "/swagger.json", "/openapi.json", "/api-docs",
  "/.env", "/.git/config", "/server-status", "/server-info",
  "/wp-json/wp/v2/users", "/graphql", "/debug", "/trace",
  "/actuator", "/actuator/health", "/info",
];

const MENTOR_THRESHOLDS = {
  repeated_action_limit: 3,
  stale_recon_steps: 10,
  no_findings_steps: 50,
  stuck_loop_unique: 5,
};

export default function AiRules() {
  const { apiFetch } = useAuth();
  const [activeAgent, setActiveAgent] = useState('exploit');
  const [agentPrompts, setAgentPrompts] = useState({ ...DEFAULT_PROMPTS });
  const [payloads, setPayloads] = useState(DEFAULT_PAYLOADS);
  const [commonPaths, setCommonPaths] = useState(DEFAULT_COMMON_PATHS);
  const [mentorThresholds, setMentorThresholds] = useState(MENTOR_THRESHOLDS);
  const [editingPayloadType, setEditingPayloadType] = useState(null);
  const [editPayloadText, setEditPayloadText] = useState('');
  const [editingPaths, setEditingPaths] = useState(false);
  const [editPathsText, setEditPathsText] = useState('');
  const [saved, setSaved] = useState(false);
  const [knowledgeStats, setKnowledgeStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/agent-scanner/rules');
      if (res.ok) {
        const data = await res.json();
        if (data.agent_prompts) setAgentPrompts(p => ({ ...DEFAULT_PROMPTS, ...data.agent_prompts }));
        else if (data.system_prompt) setAgentPrompts(p => ({ ...p, exploit: data.system_prompt }));
        if (data.payloads) setPayloads(p => ({ ...DEFAULT_PAYLOADS, ...data.payloads }));
        if (data.common_paths) setCommonPaths(data.common_paths);
        if (data.mentor_thresholds) setMentorThresholds(t => ({ ...MENTOR_THRESHOLDS, ...data.mentor_thresholds }));
      }
    } catch {}
    try {
      const res = await apiFetch('/agent-scanner/knowledge-stats');
      if (res.ok) setKnowledgeStats(await res.json());
    } catch {}
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const res = await apiFetch('/agent-scanner/rules', {
        method: 'PUT',
        body: JSON.stringify({
          agent_prompts: agentPrompts,
          payloads,
          common_paths: commonPaths,
          mentor_thresholds: mentorThresholds,
        }),
      });
      if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
      else alert('Failed to save rules');
    } catch { alert('Failed to save'); }
  };

  const resetAgent = (agentId) => {
    if (!window.confirm(`Reset ${agentId} prompt to default?`)) return;
    setAgentPrompts(p => ({ ...p, [agentId]: DEFAULT_PROMPTS[agentId] }));
  };

  const agent = AGENT_TYPES.find(a => a.id === activeAgent);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>AI Agent Rules</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={save}>{saved ? '✓ Saved' : 'Save All Rules'}</Button>
        </div>
      </div>

      {/* Knowledge Stats */}
      {knowledgeStats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Total Knowledge', value: knowledgeStats.total || 0, color: 'var(--primary)' },
            { label: 'Successful Payloads', value: knowledgeStats.successful_payload || 0, color: '#22c55e' },
            { label: 'Failed Approaches', value: knowledgeStats.failed_approach || 0, color: '#ef4444' },
            { label: 'WAF Bypasses', value: knowledgeStats.waf_bypass || 0, color: '#f59e0b' },
            { label: 'Tech Fingerprints', value: knowledgeStats.tech_fingerprint || 0, color: '#3b82f6' },
            { label: 'Human Feedback', value: knowledgeStats.human_feedback || 0, color: '#8b5cf6' },
          ].map(s => (
            <div key={s.label} style={{ padding: '12px 16px', borderRadius: 8, background: 'var(--bg-card)', border: '1px solid var(--border)', textAlign: 'center', minWidth: 100 }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Agent Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {AGENT_TYPES.map(a => (
          <button key={a.id} onClick={() => setActiveAgent(a.id)}
            style={{
              padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: activeAgent === a.id ? 700 : 500,
              color: activeAgent === a.id ? 'var(--primary)' : 'var(--text-muted)',
              borderBottom: activeAgent === a.id ? '2px solid var(--primary)' : '2px solid transparent',
              fontFamily: 'inherit', transition: 'all 0.15s',
            }}>{a.label}</button>
        ))}
      </div>

      {/* Agent System Prompt */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{agent?.label} Agent — System Prompt</h2>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0' }}>{agent?.description}</p>
          </div>
          <Button variant="secondary" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => resetAgent(activeAgent)}>Reset to Default</Button>
        </div>
        <textarea
          value={agentPrompts[activeAgent] || ''}
          onChange={e => setAgentPrompts(p => ({ ...p, [activeAgent]: e.target.value }))}
          style={{
            width: '100%', minHeight: 200, padding: 14, borderRadius: 'var(--radius)',
            border: '1px solid var(--border)', background: 'var(--bg-input)',
            color: 'var(--text)', fontSize: 13, fontFamily: 'monospace', lineHeight: 1.5,
            resize: 'vertical',
          }} />
      </Card>

      {/* Mentor Thresholds (only on Mentor tab) */}
      {activeAgent === 'mentor' && (
        <Card style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Intervention Thresholds</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { key: 'repeated_action_limit', label: 'Repeated action trigger', desc: 'Intervene after N identical actions in last 10 steps' },
              { key: 'stale_recon_steps', label: 'Stale recon trigger', desc: 'Intervene after N steps with no new endpoints' },
              { key: 'no_findings_steps', label: 'No findings trigger', desc: 'Intervene after N test steps with no findings' },
              { key: 'stuck_loop_unique', label: 'Stuck loop threshold', desc: 'Intervene when fewer than N unique actions in last 50 steps' },
            ].map(t => (
              <div key={t.key} style={{ padding: 12, borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{t.label}</label>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>{t.desc}</div>
                <input type="number" min="1" value={mentorThresholds[t.key]}
                  onChange={e => setMentorThresholds(th => ({ ...th, [t.key]: parseInt(e.target.value) || 1 }))}
                  style={{ width: 80, padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' }} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Default Payloads (only on Exploit tab) */}
      {activeAgent === 'exploit' && (
        <Card style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Default Payloads</h2>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
            Fallback payloads when the AI doesn't specify its own. The agent also learns new payloads from successful scans.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.entries(payloads).map(([type, plds]) => (
              <div key={type} style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editingPayloadType === type ? 8 : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Badge>{type.toUpperCase()}</Badge>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{plds.length} payload{plds.length !== 1 ? 's' : ''}</span>
                  </div>
                  <Button variant="secondary" style={{ fontSize: 11, padding: '3px 10px' }}
                    onClick={() => {
                      if (editingPayloadType === type) {
                        setPayloads(p => ({ ...p, [type]: editPayloadText.split('\n').filter(Boolean) }));
                        setEditingPayloadType(null);
                      } else {
                        setEditingPayloadType(type);
                        setEditPayloadText(plds.join('\n'));
                      }
                    }}>
                    {editingPayloadType === type ? 'Done' : 'Edit'}
                  </Button>
                </div>
                {editingPayloadType === type ? (
                  <textarea value={editPayloadText} onChange={e => setEditPayloadText(e.target.value)}
                    style={{ width: '100%', minHeight: 80, padding: 8, borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 12, fontFamily: 'monospace', resize: 'vertical' }} />
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                    {plds.map((p, i) => (
                      <code key={i} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{p}</code>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Common Recon Paths (only on Recon tab) */}
      {activeAgent === 'recon' && (
        <Card>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Recon Paths</h2>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
            Paths probed during reconnaissance to discover endpoints, config files, and admin panels.
          </p>
          {editingPaths ? (
            <>
              <textarea value={editPathsText} onChange={e => setEditPathsText(e.target.value)}
                style={{ width: '100%', minHeight: 200, padding: 10, borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)', fontSize: 12, fontFamily: 'monospace', resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Button onClick={() => { setCommonPaths(editPathsText.split('\n').filter(Boolean)); setEditingPaths(false); }}>Done</Button>
                <Button variant="secondary" onClick={() => setEditingPaths(false)}>Cancel</Button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                {commonPaths.map((p, i) => (
                  <code key={i} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: 'var(--bg-input)', border: '1px solid var(--border)', color: 'var(--text)' }}>{p}</code>
                ))}
              </div>
              <Button variant="secondary" style={{ fontSize: 12 }} onClick={() => { setEditingPaths(true); setEditPathsText(commonPaths.join('\n')); }}>Edit Paths</Button>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
